import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  linkWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = (() => {
  if (typeof window === "undefined") {
    return getFirestore(app);
  }
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (error) {
    console.warn("Firestore cache init failed", error);
    return getFirestore(app);
  }
})();
export const storage = getStorage(app);
export const functions = getFunctions(
  app,
  import.meta.env.VITE_FUNCTIONS_REGION || "us-central1"
);


const googleProvider = new GoogleAuthProvider();

const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
if (useEmulators) {
  const authPort = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 5002);
  connectAuthEmulator(auth, `http://localhost:${authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

export function observeAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export async function signInWithGoogle(): Promise<string> {
  if (auth.currentUser?.isAnonymous) {
    const result = await linkWithPopup(auth.currentUser, googleProvider);
    return result.user.uid;
  }
  const result = await signInWithPopup(auth, googleProvider);
  return result.user.uid;
}

export async function signOutUser(): Promise<void> {
  await signOut(auth);
}

export function waitForAuthReady(): Promise<User | null> {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });
}
